// Extra context for React hydration mismatches (#418/#423/#425).
//
// These never reach AnalyticsErrorBoundary, so the one path that records a
// componentStack (error-boundary.tsx) is bypassed and the report carries no
// component information at all. React hands hydration failures to
// `onRecoverableError(error, errorInfo)`, but Next owns that callback and its
// signature takes only the error —
// `next/dist/client/react-client-callbacks/on-recoverable-error.js` drops
// React's second argument (the errorInfo holding componentStack) and calls
// `reportError(cause)`, which surfaces to us as a plain window.onerror event.
// Next exposes no hook to intercept it, so componentStack is unrecoverable
// without patching Next internals.
//
// What we can still read is the DOM as it stood the moment React gave up.
// These fields separate the two candidate causes of an intermittent,
// page-independent mismatch: an external agent rewriting our markup before
// hydration, versus a mismatch our own render produced. Page translators wrap
// text nodes in <font> (turning a text node into an element — exactly the
// `args[]=HTML` variant) and stamp `translated-ltr`/`translated-rtl` on <html>;
// extensions inject custom elements as direct children of <body>. A clean
// snapshot points the finger back at our own markup.
//
// Collected only for hydration messages — every other error reports as before.

const HYDRATION_ERROR = /Minified React error #(418|423|425)\b|Hydration failed because|Text content does not match/

// Enough to spot injected containers without bloating the beacon payload.
const MAX_BODY_KIDS = 12
const MAX_CLASS_LEN = 120

export const hydrationContext = (message: string): Record<string, string | undefined> => {
  if (!HYDRATION_ERROR.test(message) || typeof document === 'undefined') {
    return {}
  }

  const root = document.documentElement
  const kids = Array.from(document.body?.children ?? [])

  return {
    // Chrome/Edge translate stamps `translated-ltr` here; a bare value means
    // no translator ran.
    htmlClass: root.className.slice(0, MAX_CLASS_LEN) || undefined,
    htmlLang: root.lang || undefined,
    // Non-zero on any page a translator has rewritten.
    fontTags: String(document.getElementsByTagName('font').length),
    // Tag names of <body>'s direct children — extension roots show up here as
    // custom elements (e.g. grammarly-extension) that our markup never emits.
    bodyKids: kids
      .slice(0, MAX_BODY_KIDS)
      .map((el) => el.tagName.toLowerCase())
      .join(','),
    bodyKidCount: String(kids.length),
    readyState: document.readyState,
  }
}
